/**
 * Summarizer Agent
 * Creates human-readable summaries of each agent's output
 * Each agent has a specific prompt tailored to its "character"
 */

import { callLLM, isProviderConfigured } from '@services/llm'

/**
 * Fast/cheap models for each provider (used for summaries)
 * These are optimized for quick, low-cost operations
 */
const FAST_MODELS = {
  claude: 'haiku',         // Claude 3.5 Haiku - fast & cheap
  openai: 'gpt-4o-mini',   // GPT-4o Mini - budget option
  gemini: 'flash-lite',    // Gemini Flash Lite - lightweight
}

/**
 * Agent-specific prompts that match each agent's "character"
 */
const AGENT_PROMPTS = {
  serp: `Tu resumes les resultats d'une analyse SERP (recherche web).

MONTRE:
- Le provider utilise (Perplexity/Claude/GPT/Gemini) si mentionne
- Les sources E-E-A-T trouvees (URLs reelles)
- Les types de contenus dominants (videos, articles, local pack...)
- Les opportunites SEO identifiees

FORMAT JSON:
{
  "headline": "X sources via [Provider]",
  "bullets": ["Source 1: url.com", "Source 2: url.com", "Type dominant: X"],
  "keyMetric": {"label": "Provider", "value": "Perplexity|Claude|GPT|Gemini"},
  "sources": ["url1", "url2", "url3"],
  "searchIntent": "informationnel/transactionnel/local"
}`,

  stratege: `Tu résumes la stratégie SEO définie par l'agent Stratège.

MONTRE:
- Le TYPE DE SITE recommandé (guide, expert-local, blog, comparatif...)
- Le POSITIONNEMENT choisi (USP)
- La CIBLE (ICP) identifiée
- L'ANGLE différenciant

FORMAT JSON:
{
  "headline": "Site [TYPE] pour [CIBLE]",
  "bullets": ["🎯 Type: X", "👤 Cible: X", "💡 Angle: X", "🏆 USP: X"],
  "keyMetric": {"label": "Type de site", "value": "X"},
  "siteType": "guide|expert-local|blog|comparatif",
  "target": "description courte de la cible",
  "usp": "proposition de valeur unique"
}`,

  identite: `Tu résumes l'identité/persona créé par l'agent Identité.

MONTRE:
- Le NOM du persona (prénom, nom)
- Son PROFIL (métier, expérience)
- Son TON éditorial (chaleureux, expert, accessible...)
- Sa BACKSTORY (déclencheur, mission)

FORMAT JSON:
{
  "headline": "[Prénom Nom], [profil court]",
  "bullets": ["👤 [Prénom Nom]", "💼 [Métier/Expérience]", "🗣️ Ton: [style]", "📖 [Mission courte]"],
  "keyMetric": {"label": "Persona", "value": "[Prénom]"},
  "persona": {"name": "X", "profile": "X", "experience": "X ans"},
  "tone": "chaleureux|expert|accessible|technique",
  "backstory": "résumé court"
}`,

  architecte: `Tu résumes l'architecture du site créée par l'agent Architecte.

MONTRE:
- Le NOMBRE de pages par tier (T1, T2, T3, T4)
- Les PAGES PRINCIPALES (titres)
- La STRUCTURE de navigation
- Le MAILLAGE interne

FORMAT JSON:
{
  "headline": "X pages en Y silos",
  "bullets": ["📄 T1: [pages principales]", "📑 T2: X pages", "🔗 Maillage: X liens internes"],
  "keyMetric": {"label": "Pages totales", "value": "X"},
  "pages": [{"name": "X", "tier": "T1"}, ...],
  "structure": "description courte"
}`,

  redacteur: `Tu résumes les briefs de contenu créés par l'agent Rédacteur.

MONTRE:
- Le NOMBRE de briefs créés
- Les MOTS-CLÉS principaux
- Le NOMBRE de FAQs/FLUQs
- La LONGUEUR moyenne visée

FORMAT JSON:
{
  "headline": "X briefs, Y FAQs",
  "bullets": ["📝 X briefs détaillés", "🔑 KW: [mot-clé 1], [mot-clé 2]", "❓ Y FAQs", "📏 ~Z mots/page"],
  "keyMetric": {"label": "Briefs", "value": "X"},
  "keywords": ["kw1", "kw2", "kw3"],
  "faqCount": X,
  "avgWordCount": X
}`,

  da: `Tu résumes la direction artistique créée par l'agent DA.

MONTRE:
- Les COULEURS (hex avec noms)
- Les POLICES choisies
- Le STYLE visuel (moderne, épuré, chaleureux...)
- Les COMPOSANTS Tailwind clés

FORMAT JSON:
{
  "headline": "Style [adjectif] en [couleur principale]",
  "bullets": ["🎨 Primaire: #XXX ([nom])", "🎨 Accent: #XXX", "✏️ Titres: [Police]", "✏️ Corps: [Police]"],
  "keyMetric": {"label": "Couleur", "value": "#XXX"},
  "colors": {"primary": "#XXX", "secondary": "#XXX", "accent": "#XXX"},
  "fonts": {"heading": "X", "body": "Y"},
  "style": "moderne|épuré|chaleureux|professionnel"
}`,

  content: `Tu résumes les pages HTML générées par l'agent Content Writer.

MONTRE:
- Le NOMBRE de fichiers générés
- Les NOMS des fichiers
- Les TECHNOLOGIES utilisées (Tailwind, Schema.org...)
- Le STATUT (complet, responsive...)

FORMAT JSON:
{
  "headline": "X pages HTML générées",
  "bullets": ["💻 X fichiers .html", "🏠 index.html + Y pages", "🏷️ Schema.org intégré", "📱 Responsive"],
  "keyMetric": {"label": "Fichiers", "value": "X"},
  "files": ["index.html", "page2.html", ...],
  "technologies": ["Tailwind", "Schema.org"]
}`,

  images: `Tu résumes les images générées par l'agent Images.

MONTRE:
- Le NOMBRE d'images générées
- Les TYPES (hero, article, thumbnail...)
- Les DIMENSIONS
- Le STYLE visuel

FORMAT JSON:
{
  "headline": "X images générées",
  "bullets": ["🖼️ X images créées", "🎭 Style: [description]", "📏 Formats optimisés"],
  "keyMetric": {"label": "Images", "value": "X"},
  "images": [{"name": "X", "type": "hero"}],
  "style": "description du style"
}`
}

/**
 * Generate a visual summary of an agent's output
 */
export async function summarize(phase, result, options = {}) {
  const { provider = 'claude', onProgress } = options

  const fastModel = FAST_MODELS[provider] || 'haiku'
  console.log(`📊 [SUMMARIZER] Starting summary for ${phase}, provider: ${provider}, model: ${fastModel}`)

  // Check if provider is configured
  if (!isProviderConfigured(provider)) {
    console.log(`📊 [SUMMARIZER] Provider ${provider} not configured, using fallback`)
    return generateFallbackSummary(phase, result)
  }

  const agentPrompt = AGENT_PROMPTS[phase]
  if (!agentPrompt) {
    console.log(`📊 [SUMMARIZER] No specific prompt for ${phase}, using fallback`)
    return generateFallbackSummary(phase, result)
  }

  // For images phase, use fallback directly - we have exact counts
  // AI tends to get wrong counts due to data truncation
  if (phase === 'images') {
    console.log(`📊 [SUMMARIZER] Using fallback for images (exact counts: ${result.images?.length || 0})`)
    return generateFallbackSummary(phase, result)
  }

  const prompt = `${agentPrompt}

DONNÉES DE L'AGENT:
\`\`\`json
${JSON.stringify(result, null, 2).slice(0, 4000)}
\`\`\`

Réponds UNIQUEMENT avec le JSON valide, sans markdown ni texte avant/après.`

  try {
    onProgress?.({ message: 'Génération du résumé...' })

    const response = await callLLM({
      provider,
      model: fastModel,  // Uses fast/cheap model for the provider
      messages: [{ role: 'user', content: prompt }],
      systemPrompt: 'Tu extrais et résumes les données clés en JSON. Sois précis et utilise les vraies valeurs des données.',
      maxTokens: 800,
    })

    const text = response.content || ''
    const jsonMatch = text.match(/\{[\s\S]*\}/)

    if (jsonMatch) {
      const aiSummary = JSON.parse(jsonMatch[0])
      console.log(`📊 [SUMMARIZER] ✅ AI summary for ${phase}:`, aiSummary.headline)

      // IMPORTANT: Merge AI summary with real data from fallback
      // AI is good for headlines/bullets but truncates structured data (pages, sources, etc.)
      const fallbackData = generateFallbackSummary(phase, result)

      // Use AI for text, fallback for structured data
      const merged = {
        success: true,
        phase,
        headline: aiSummary.headline || fallbackData.headline,
        bullets: aiSummary.bullets || fallbackData.bullets,
        keyMetric: aiSummary.keyMetric || fallbackData.keyMetric,
        // Always use fallback for structured data (complete, not truncated)
        ...extractStructuredData(fallbackData),
      }

      console.log(`📊 [SUMMARIZER] Merged summary for ${phase}, pages:`, merged.pages?.length || 'N/A')
      return merged
    }

    throw new Error('No JSON found')
  } catch (error) {
    console.error(`📊 [SUMMARIZER] ❌ Error for ${phase}:`, error.message)
    return generateFallbackSummary(phase, result)
  }
}

/**
 * Extract structured data fields from fallback (pages, sources, colors, etc.)
 */
function extractStructuredData(fallback) {
  const structured = {}
  const structuredFields = ['sources', 'pages', 'colors', 'fonts', 'files', 'keywords', 'images', 'persona', 'siteType', 'target', 'usp', 'tone', 'backstory', 'structure', 'faqCount', 'style', 'technologies', 'searchIntent', '_debug']

  for (const field of structuredFields) {
    if (fallback[field] !== undefined) {
      structured[field] = fallback[field]
    }
  }

  return structured
}

/**
 * Generate a fallback summary without LLM - extracts real data from results
 */
function generateFallbackSummary(phase, result) {
  const summaries = {
    serp: () => {
      const sources = result.serp?.sources || result.sources || []
      const searchProvider = result.searchProvider || result.serp?.searchProvider || 'auto'
      const providerNames = { perplexity: 'Perplexity', claude: 'Claude', openai: 'GPT', gemini: 'Gemini' }
      const providerLabel = providerNames[searchProvider] || searchProvider
      const intent = result.serp?.intent || result.serp?.dominantIntent || 'mixte'
      return {
        headline: `${sources.length} sources analysées via ${providerLabel}`,
        bullets: sources.slice(0, 3).map(s => `${s}`),
        keyMetric: { label: "Provider", value: providerLabel },
        // Rich metrics for UI display
        metrics: [
          { icon: '🔍', label: 'Provider', value: providerLabel },
          { icon: '📊', label: 'Sources', value: sources.length.toString() },
          { icon: '🎯', label: 'Intent', value: intent },
        ],
        // Structured data
        sources: sources.slice(0, 6),
        searchIntent: intent,
        searchProvider,
      }
    },

    stratege: () => {
      const domain = result.domain || {}
      const strategy = result.strategy || {}
      const target = strategy.target || result.target || {}
      const faqs = strategy.faqs || []
      const fluqs = strategy.fluqs || {}
      const fluqCount = Object.values(fluqs).flat().length
      const siteType = domain.naturalAngle || strategy.siteType || 'guide'
      const usp = strategy.positioning?.usp || strategy.usp || ''
      const targetDesc = target.demographics?.situation || target.realGoal || ''

      return {
        headline: `Site ${siteType} pour ${domain.location || 'France'}`,
        bullets: [
          `🎯 Type: ${siteType}`,
          `👤 Cible: ${targetDesc.slice(0, 50) || 'définie'}`,
          `💡 Angle: ${domain.recommendedMask || 'expert'}`,
          `🏆 USP: ${usp.slice(0, 50) || 'établie'}...`,
        ],
        keyMetric: { label: "Type", value: siteType },
        // Rich metrics for UI display
        metrics: [
          { icon: '🎯', label: 'Type site', value: siteType },
          { icon: '👤', label: 'Cible', value: targetDesc.slice(0, 30) || 'définie' },
          { icon: '❓', label: 'FAQs', value: faqs.length.toString() },
          { icon: '🔮', label: 'FLUQs', value: fluqCount.toString() },
        ],
        // Structured data
        siteType,
        target: targetDesc,
        usp,
        faqCount: faqs.length,
        fluqCount,
      }
    },

    identite: () => {
      const identity = result.identity || {}
      const mask = identity.mask || identity.masque || {}
      const profile = mask.profile || {}
      const naming = identity.naming || {}
      const tone = identity.editorial?.tone || identity.ton || {}
      const backstory = identity.backstory || {}

      // Extract name properly
      const firstName = profile.firstName || mask.prenom || ''
      const lastName = profile.lastName || mask.nom || ''
      const fullName = naming.authorName || `${firstName} ${lastName}`.trim() || 'Persona'

      // Extract profile details
      const age = profile.ageRange || mask.ageRange || ''
      const location = profile.location || mask.location || ''
      const background = profile.background || mask.profession || profile.currentSituation || ''
      const expertiseLevel = mask.expertiseLevel || 'expert'

      // Extract tone details
      const register = tone.register || tone.registre || 'conversationnel'
      const warmth = tone.warmth || 'neutre'
      const technicality = tone.technicality || 'accessible'

      // Extract mission/trigger
      const mission = mask.mission || ''
      const trigger = backstory.trigger || ''

      return {
        headline: `${fullName}${age ? `, ${age}` : ''}${location ? ` • ${location}` : ''}`,
        bullets: [
          `👤 ${fullName}`,
          `💼 ${background.slice(0, 50) || 'Expert du domaine'}`,
          `🗣️ Ton: ${register}`,
          `📖 ${trigger.slice(0, 40) || mission.slice(0, 40) || 'Mission définie'}...`,
        ],
        keyMetric: { label: "Persona", value: firstName || fullName.split(' ')[0] },
        // Rich metrics for UI display
        metrics: [
          { icon: '👤', label: 'Persona', value: fullName },
          { icon: '💼', label: 'Profil', value: background.slice(0, 25) || expertiseLevel },
          { icon: '🗣️', label: 'Ton', value: `${register} • ${warmth}` },
          { icon: '📖', label: 'Trigger', value: trigger.slice(0, 30) || 'défini' },
        ],
        // Structured data
        persona: {
          name: fullName,
          firstName,
          lastName,
          age,
          location,
          background,
          expertiseLevel,
        },
        tone: { register, warmth, technicality },
        mission,
        trigger,
        backstory: backstory.fullText || trigger,
      }
    },

    architecte: () => {
      const pages = result.pages || result.architecture?.pages || []
      const architecture = result.architecture || {}
      const t1Pages = pages.filter(p => p.tier === 'T1' || p.tier === 1 || p.level === 1)
      const t2Pages = pages.filter(p => p.tier === 'T2' || p.tier === 2 || p.level === 2)
      const t3Pages = pages.filter(p => p.tier === 'T3' || p.tier === 3 || p.level === 3)
      const t4Pages = pages.filter(p => p.tier === 'T4' || p.tier === 4 || p.level === 4)
      const mechanic = architecture.mechanic || 'silo'

      console.log('📊 [SUMMARIZER] Architecte pages:', pages.length, pages.map(p => p.name || p.file))

      return {
        headline: `${pages.length} pages en structure ${mechanic}`,
        bullets: [
          `📄 T1 (piliers): ${t1Pages.map(p => p.name || p.title).slice(0, 3).join(', ') || 'Accueil'}`,
          `📑 T2 (services): ${t2Pages.length} pages`,
          `📎 T3 (support): ${t3Pages.length} pages`,
          `🔗 Total: ${pages.length} pages à générer`,
        ],
        keyMetric: { label: "Pages", value: pages.length.toString() },
        // Rich metrics for UI display
        metrics: [
          { icon: '🏗️', label: 'Structure', value: mechanic },
          { icon: '📄', label: 'T1', value: t1Pages.length.toString() },
          { icon: '📑', label: 'T2', value: t2Pages.length.toString() },
          { icon: '📎', label: 'T3/T4', value: (t3Pages.length + t4Pages.length).toString() },
        ],
        // Structured data
        pages: pages.map(p => ({
          name: p.name || p.title || p.file,
          tier: p.tier || `T${p.level || 2}`,
          file: p.file || `${p.slug || 'page'}.html`
        })),
        structure: mechanic,
        tierBreakdown: { t1: t1Pages.length, t2: t2Pages.length, t3: t3Pages.length, t4: t4Pages.length },
        _debug: {
          sourceField: result.pages ? 'result.pages' : 'result.architecture.pages',
          rawCount: pages.length
        }
      }
    },

    redacteur: () => {
      const briefs = result.briefs?.pages || result.briefs || []
      const briefsArray = Array.isArray(briefs) ? briefs : Object.values(briefs)
      const keywords = briefsArray.flatMap(b => [b.seo?.primaryKw, ...(b.seo?.secondaryKw || [])]).filter(Boolean)
      const uniqueKeywords = [...new Set(keywords)].slice(0, 5)
      const faqCount = briefsArray.reduce((sum, b) => sum + (b.faqs?.length || 0), 0)
      const avgWordCount = briefsArray.length > 0
        ? Math.round(briefsArray.reduce((sum, b) => sum + (b.structure?.sections?.reduce((s, sec) => s + (sec.wordCount || 300), 0) || 800), 0) / briefsArray.length)
        : 800
      const totalWords = avgWordCount * briefsArray.length

      return {
        headline: `${briefsArray.length} briefs • ~${Math.round(totalWords/1000)}k mots`,
        bullets: [
          `📝 ${briefsArray.length} briefs détaillés`,
          `🔑 KW: ${uniqueKeywords.slice(0, 3).join(', ') || 'définis'}`,
          `❓ ${faqCount} FAQs`,
        ],
        keyMetric: { label: "Briefs", value: briefsArray.length.toString() },
        // Rich metrics for UI display
        metrics: [
          { icon: '📝', label: 'Briefs', value: briefsArray.length.toString() },
          { icon: '📏', label: 'Mots total', value: `~${Math.round(totalWords/1000)}k` },
          { icon: '❓', label: 'FAQs', value: faqCount.toString() },
          { icon: '🔑', label: 'Keywords', value: uniqueKeywords.length.toString() },
        ],
        // Structured data
        keywords: uniqueKeywords,
        faqCount,
        avgWordCount,
        totalWords,
      }
    },

    da: () => {
      const design = result.design || result
      const colors = design.colors || {}
      const typo = design.typography || {}
      const direction = design.direction || {}

      // Extract colors with fallbacks
      const primaryHex = colors.primary?.hex || '#2563eb'
      const secondaryHex = colors.secondary?.hex || '#1e40af'
      const accentHex = colors.accent?.hex || '#f59e0b'

      // Extract fonts
      const headingFont = typo.heading?.family || 'Montserrat'
      const bodyFont = typo.body?.family || 'Open Sans'

      // Extract style
      const style = direction.targetFeeling || design.style || 'moderne'

      return {
        headline: `Style ${style}`,
        bullets: [
          `🎨 Primaire: ${primaryHex}`,
          `🎨 Accent: ${accentHex}`,
          `✏️ Titres: ${headingFont}`,
          `✏️ Corps: ${bodyFont}`,
        ],
        keyMetric: { label: "Style", value: style },
        // Rich metrics for UI display (with actual colors!)
        metrics: [
          { icon: '🎨', label: 'Palette', value: [primaryHex, secondaryHex, accentHex], isPalette: true },
          { icon: '✏️', label: 'Typo titres', value: headingFont },
          { icon: '📝', label: 'Typo corps', value: bodyFont },
          { icon: '🎯', label: 'Style', value: style },
        ],
        // Structured data
        colors: {
          primary: primaryHex,
          secondary: secondaryHex,
          accent: accentHex,
        },
        fonts: {
          heading: headingFont,
          body: bodyFont,
        },
        style,
      }
    },

    content: () => {
      const files = result.files ? Object.keys(result.files) : []
      const htmlFiles = files.filter(f => f.endsWith('.html'))
      return {
        headline: `${htmlFiles.length} pages HTML générées`,
        bullets: [
          `💻 ${htmlFiles.length} fichiers .html`,
          `🏠 ${files.includes('index.html') ? 'index.html' : files[0] || 'home'} + ${htmlFiles.length - 1} pages`,
          `🏷️ Schema.org intégré`,
          `📱 Responsive Tailwind`,
        ],
        keyMetric: { label: "Fichiers", value: htmlFiles.length.toString() },
        // Rich metrics for UI display
        metrics: [
          { icon: '💻', label: 'Pages', value: htmlFiles.length.toString() },
          { icon: '🏷️', label: 'Schema', value: 'LocalBusiness' },
          { icon: '🎨', label: 'CSS', value: 'Tailwind' },
          { icon: '📱', label: 'Mobile', value: 'Responsive' },
        ],
        // Structured data
        files: files.slice(0, 10),
        technologies: ['Tailwind CSS', 'Schema.org', 'HTML5'],
      }
    },

    images: () => {
      const images = result.images || []
      const style = result.style || 'professionnel'
      const types = [...new Set(images.map(img => img.type || 'image'))]
      return {
        headline: `${images.length} images générées`,
        bullets: [
          `🖼️ ${images.length} images créées`,
          `🎭 Style: ${style}`,
          `📏 Formats WebP optimisés`,
        ],
        keyMetric: { label: "Images", value: images.length.toString() },
        // Rich metrics for UI display
        metrics: [
          { icon: '🖼️', label: 'Images', value: images.length.toString() },
          { icon: '🎭', label: 'Style', value: style },
          { icon: '📐', label: 'Types', value: types.slice(0, 2).join(', ') || 'variés' },
        ],
        // Structured data
        images: images.slice(0, 5).map(img => ({ name: img.name || img, type: img.type || 'image' })),
        style,
        types,
      }
    },
  }

  const generator = summaries[phase]
  if (generator) {
    return { success: true, phase, ...generator() }
  }

  return {
    success: true,
    phase,
    headline: "Phase terminée",
    bullets: ["✅ Traitement effectué"],
    keyMetric: { label: "Statut", value: "OK" },
  }
}

/**
 * Batch summarize multiple phase results
 */
export async function summarizeAll(context, options = {}) {
  const summaries = {}

  for (const [phase, result] of Object.entries(context)) {
    if (result && typeof result === 'object') {
      summaries[phase] = await summarize(phase, result, options)
    }
  }

  return summaries
}

export default { summarize, summarizeAll }
